/**
 * useSelectedProperty Hook
 *
 * Purpose:
 * Manages the currently selected property for the homeowner view.
 * This hook provides a centralized way to track which property is currently
 * selected and persists the selection across browser sessions.
 *
 * Usage Example:
 * ```tsx
 * import { useSelectedProperty } from '../features/properties/hooks/useSelectedProperty';
 *
 * function MyComponent() {
 *   const { selectedPropertyId, setSelectedPropertyId } = useSelectedProperty();
 *
 *   return <div>Content for property {selectedPropertyId}</div>;
 * }
 * ```
 *
 * Key Features:
 * - Persists selection to localStorage for session continuity
 * - Provides context to all components via PropertyProvider
 * - Type-safe with TypeScript
 * - Throws error if used outside PropertyProvider
 */

import { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react';
import { getProfileRole } from '@/services/auth/auth.service';
import { homeOwnerObjectsService } from '@/services/supabase/homeOwnerObjects.service';
import { propertyAssignmentsService } from '@/services/supabase/propertyAssignments.service';
import { employeesService } from '@/services/supabase/employees.service';

const STORAGE_KEY = 'zentrian_selected_property';
const STORAGE_KEY_NAME = 'zentrian_selected_property_name';

const getInitialSelectedProperty = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const storedPropertyId = window.localStorage.getItem(STORAGE_KEY);
    return storedPropertyId || null;
  } catch (error) {
    console.error('Failed to read selected property ID from localStorage', error);
  }
  return null;
};

const getInitialSelectedPropertyName = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const storedPropertyName = window.localStorage.getItem(STORAGE_KEY_NAME);
    return storedPropertyName || null;
  } catch (error) {
    console.error('Failed to read selected property name from localStorage', error);
  }
  return null;
};

interface PropertyContextValue {
  selectedPropertyId: string | null;
  selectedPropertyName: string | null;
  setSelectedPropertyId: (propertyId: string | null) => void;
  setSelectedPropertyName: (propertyName: string | null) => void;
}

const PropertyContext = createContext<PropertyContextValue | undefined>(undefined);

export const PropertyProvider = ({ children }: { children: ReactNode }) => {
  const [selectedPropertyId, setSelectedPropertyIdState] = useState<string | null>(getInitialSelectedProperty);
  const [selectedPropertyName, setSelectedPropertyNameState] = useState<string | null>(getInitialSelectedPropertyName);
  const [isAutoSelectDone, setIsAutoSelectDone] = useState(false);

  // Auto-select first property if none is selected
  useEffect(() => {
    const autoSelectProperty = async () => {
      // Only auto-select if no property is currently selected and we haven't tried yet
      if (selectedPropertyId !== null || isAutoSelectDone) {
        return;
      }

      try {
        const role = await getProfileRole();

        if (!role) {
          console.log('[PropertyProvider] No user role, skipping auto-select');
          setIsAutoSelectDone(true);
          return;
        }

        console.log('[PropertyProvider] Auto-selecting property for role:', role);

        let properties: any[] = [];

        // Fetch properties based on role
        if (role === 'employee') {
          properties = await employeesService.getAssignedPropertiesForEmployee();
        } else if (role === 'technician') {
          properties = await propertyAssignmentsService.getAssignedPropertiesForServiceProvider();
        } else {
          // Homeowners, members, and admins
          properties = await homeOwnerObjectsService.getAll();
        }

        console.log('[PropertyProvider] Properties fetched:', properties.length);

        // Auto-select first property if any exist
        if (properties.length > 0) {
          const firstProperty = properties[0];
          console.log('[PropertyProvider] Auto-selecting first property:', firstProperty.id, firstProperty.name);
          setSelectedPropertyIdState(firstProperty.id);
          setSelectedPropertyNameState(firstProperty.name);
        }
      } catch (error) {
        console.error('[PropertyProvider] Error during auto-select:', error);
      } finally {
        setIsAutoSelectDone(true);
      }
    };

    autoSelectProperty();
  }, [selectedPropertyId, isAutoSelectDone]);

  useEffect(() => {
    try {
      if (selectedPropertyId === null) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, selectedPropertyId);
      }
    } catch (error) {
      console.error('Failed to save selected property ID to localStorage', error);
    }
  }, [selectedPropertyId]);

  useEffect(() => {
    try {
      if (selectedPropertyName === null) {
        window.localStorage.removeItem(STORAGE_KEY_NAME);
      } else {
        window.localStorage.setItem(STORAGE_KEY_NAME, selectedPropertyName);
      }
    } catch (error) {
      console.error('Failed to save selected property name to localStorage', error);
    }
  }, [selectedPropertyName]);

  const setSelectedPropertyId = useCallback((propertyId: string | null) => {
    setSelectedPropertyIdState(propertyId);
  }, []);

  const setSelectedPropertyName = useCallback((propertyName: string | null) => {
    setSelectedPropertyNameState(propertyName);
  }, []);

  const value: PropertyContextValue = {
    selectedPropertyId,
    selectedPropertyName,
    setSelectedPropertyId,
    setSelectedPropertyName,
  };

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
};

export const useSelectedProperty = () => {
  const context = useContext(PropertyContext);
  if (context === undefined) {
    throw new Error('useSelectedProperty must be used within a PropertyProvider');
  }
  return context;
};

export const useOptionalSelectedProperty = () => {
  return useContext(PropertyContext);
};
